<template>
    <div class="config-visibility-group">
        <div class="config-visibility-card rounder-8 mb-4">
            <div class="filters-summary-header">
                <span class="filters-summary-icon">
                    <font-awesome-icon :icon="['fas', 'filter']" />
                </span>
                <span class="filters-summary-title">Filtros de productos:</span>
            </div>

            <div class="col-12 mb-3" v-if="authentication_user.active_store">
                <label class="form-label">{{ getFilterLabel('sku') }}</label>
                <div class="sku-input-wrapper">
                    <div class="sku-input-container">
                        <input 
                            type="text" 
                            class="form-control sku-input" 
                            v-model="skuInput"
                            placeholder="Buscar por SKU (Enter)"
                            @keydown.enter.prevent="addSkuTag"
                        />
                        <font-awesome-icon 
                            v-if="skuInput !== ''"
                            :icon="['fas', 'times']" 
                            class="clear-icon cursor-pointer"
                            @click="skuInput = ''"
                        />
                    </div>
                </div>
                <div v-if="selectedSkus.length > 0" class="tags-wrapper mt-2">
                    <div class="tags-container">
                        <span 
                            v-for="(sku, index) in selectedSkus.slice(0, 10)" 
                            :key="index"
                            class="tag"
                        >
                            {{ sku }}
                            <font-awesome-icon 
                                :icon="['fas', 'times']" 
                                class="tag-remove-icon"
                                @click="removeSkuTag(index)"
                            />
                        </span>
                        <span v-if="selectedSkus.length > 10" class="tag tag-more" :title="selectedSkus.slice(10).join(', ')">
                            +{{ selectedSkus.length - 10 }} más...
                        </span>
                    </div>
                    <button class="btn-clear-all" @click="clearSkus" title="Eliminar todos">
                        <font-awesome-icon :icon="['fas', 'trash']" />
                    </button>
                </div>
            </div>

            <div class="row g-3 mb-3">
                <div class="col-6" v-if="shouldShowFilter('supplier')">
                    <label class="form-label">{{ getFilterLabel('supplier') }}</label>
                    <v-select
                        :options="proveedores"
                        v-model="localFilters.supplier"
                        :reduce="proveedor => proveedor"
                        :placeholder="`Seleccionar ${getFilterLabel('supplier').toLowerCase()}`"
                        class="custom-v-select"
                        :clearable="true"
                        @input="onClearSelect('supplier', $event)"
                    />
                </div>
                <div class="col-6" v-if="shouldShowFilter('brand')">
                    <label class="form-label">{{ getFilterLabel('brand') }}</label>
                    <v-select
                        :options="marcas"
                        v-model="localFilters.brand"
                        :reduce="marca => marca"
                        :placeholder="`Seleccionar ${getFilterLabel('brand').toLowerCase()}`"
                        class="custom-v-select"
                        :clearable="true"
                        @input="onClearSelect('brand', $event)"
                    />
                </div>
                <div class="col-6" v-if="shouldShowFilter('select_category')">
                    <label class="form-label">{{ getFilterLabel('select_category') }}</label>
                    <filter-categories @getData="getCategory" class="custom-select" :category="localFilters.select_category"/>
                </div>
                <div class="col-6" v-if="shouldShowFilter('product_group')">
                    <label class="form-label">{{ getFilterLabel('product_group') }}</label>
                    <v-select
                        :options="groups"
                        v-model="localFilters.product_group"
                        :reduce="group => group"
                        :placeholder="`Seleccionar ${getFilterLabel('product_group').toLowerCase()}`"
                        class="custom-v-select"
                        :clearable="true"
                        @input="onClearSelect('product_group', $event)"
                    />
                </div>
                <div class="col-6" v-if="shouldShowFilter('product_type')">
                    <label class="form-label">{{ getFilterLabel('product_type') }}</label>
                    <v-select
                        :options="filtros"
                        v-model="localFilters.product_type"
                        :reduce="filtro => filtro"
                        :placeholder="`Seleccionar ${getFilterLabel('product_type').toLowerCase()}`"
                        class="custom-v-select"
                        :clearable="true"
                        @input="onClearSelect('product_type', $event)"
                    />
                </div>
                <div class="col-6" v-if="authentication_user.active_store">
                    <label class="form-label">{{ getFilterLabel('store') }}</label>
                    <v-select
                        :options="clusters"
                        v-model="localFilters.store"
                        label="name"
                        :reduce="store => store.name"
                        :placeholder="`Seleccionar ${getFilterLabel('store').toLowerCase()}`"
                        class="custom-v-select"
                        :clearable="true"
                        @input="onClearSelect('store', $event)"
                    />
                </div>
            </div>

            <div class="divider-line"></div>

            <div class="active-filters-list" v-if="activeFilters.length > 0">
                <span 
                    v-for="(filter, index) in activeFilters" 
                    :key="index"
                    class="filter-chip"
                >
                    <span class="filter-chip-label">{{ filter.label }}</span>
                    <span class="filter-chip-value">{{ filter.value }}</span>
                </span>
            </div>
            <div v-else class="no-filters-msg">
                <span class="text-secondary size-13">Se deben aplicar filtros</span>
            </div>

            <div class="divider-line"></div>

            <div class="row g-3 mb-4">
                <div class="col-6">
                    <label class="form-label fw-600 size-13 text-secondary mb-2">VISIBILIDAD</label>
                    <div class="input-group">
                        <select 
                            class="form-control fw-500 size-16" 
                            v-model="visibility"
                        >
                            <option value="" disabled>Seleccione una opción</option>
                            <option v-for="option in visibilityOptions" :key="option" :value="option">{{ option }}</option>
                        </select>
                    </div>
                    <small class="text-secondary size-12 mt-2 d-block">Define la clasificación de visibilidad para los productos que coincidan con los filtros</small>
                </div>
                <div class="col-6">
                    <label class="form-label fw-600 size-13 text-secondary mb-2">VISIBILIDAD (N)</label>
                    <div class="input-group">
                        <input 
                            type="number" 
                            class="form-control fw-500 size-16" 
                            v-model="visibility_number"
                            placeholder="Ingrese el valor numérico"
                            step="0.01"
                        />
                    </div>
                    <small class="text-secondary size-12 mt-2 d-block">Valor numérico de visibilidad</small>
                </div>
            </div>
        </div>

        <div class="d-flex justify-content-end gap-2">
            <button class="btn btn-success fw-400 px-4" @click="saveVisibilityGroup" :disabled="saving || !isFormValid">
                <span v-if="saving">Guardando...</span>
                <span v-else>Guardar</span>
            </button>
        </div>
    </div>
</template>

<script>
import { mapActions,mapGetters } from 'vuex';
import configsRecGroupFiltersMixin from '../mixins/configsRecGroupFiltersMixin';
export default {
    name: 'config-visibility-group',
    mixins: [configsRecGroupFiltersMixin],
    props: {
        propHead: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            visibility: '',
            visibility_number: null,
            saving: false,
            visibilityOptions: [
                'A0', 'A1', 'A2', 'A3', 'A4', 'A5',
                'B1', 'B2', 'B3', 'B4', 'B5',
                'C1', 'C2', 'C3', 'C4', 'C5',
                'D1', 'D2', 'D3', 'D4', 'D5',
                'E1','X1','X2','X3','X4'
            ],
        };
    },
    computed: {
        ...mapGetters([
            "authentication_user",
        ]),
        isFormValid() {
            return this.visibility !== '' && 
                   this.visibility_number !== null && 
                   this.visibility_number !== undefined && 
                   this.visibility_number !== '';
        },
    },
    methods: {
        ...mapActions(['update']),
        saveVisibilityGroup() {
            if (!this.isFormValid) {
                this.$notification.error('Todos los campos de visibilidad son obligatorios.', { timer: 5, showCloseIcn: true });
                return;
            }

            const activeFilterParams = {};
            this.FILTER_KEYS.forEach(key => {
                if (key === 'sku') {
                    if (this.selectedSkus.length > 0) {
                        activeFilterParams[key] = this.selectedSkus.join(',');
                    }
                } else {
                    const val = this.localFilters[key];
                    if (val !== null && val !== undefined && val !== '' && val !== 0) {
                        activeFilterParams[key] = val;
                    }
                }
            });

            this.saving = true;
            const data = {
                visibility: this.visibility,
                visibility_number: this.visibility_number,
                filters: activeFilterParams,
            };

            this.update({
                url: `/api/products/config_visibility/update_group/`,
                params: data,
            }).then(() => {
                this.saving = false;
                this.$emit('refrest');
                this.$notification.success('Visibilidad actualizada correctamente para los productos filtrados.', { timer: 5, showCloseIcn: true });
            }).catch((error) => {
                this.saving = false;
                if (error.status == 400) {
                    this.$notification.error(error.data.message || 'Error al guardar la visibilidad.', { timer: 5, showCloseIcn: true });
                } else {
                    this.$notification.error('Error al guardar la visibilidad.', { timer: 5, showCloseIcn: true });
                }
            });
        },
    }
};
</script>

<style scoped>
.config-visibility-card {
    background: #ffffff;
}

.filters-summary-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
}

.filters-summary-icon {
    color: #9ca3af;
    font-size: 0.85rem;
}

.filters-summary-title {
    font-size: 0.85rem;
    font-weight: 600;
    color: #6b7280;
}

.active-filters-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 16px;
}

.filter-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #f3f4f6;
    border-radius: 6px;
    padding: 4px 10px;
    font-size: 0.8rem;
}

.filter-chip-label {
    color: #9ca3af;
    font-weight: 500;
}

.filter-chip-value {
    color: #374151;
    font-weight: 600;
}

.no-filters-msg {
    padding: 8px 0 16px;
}

.divider-line {
    height: 1px;
    background: #e5e7eb;
    margin-bottom: 20px;
}

.form-control:focus {
    border-color: #1AA179;
    box-shadow: 0 0 0 0.2rem rgba(26, 161, 121, 0.25);
}

.btn-success {
    background-color: #1AA179 !important;
    border-color: #1AA179 !important;
}

.btn-success:hover:not(:disabled) {
    background-color: #158a65 !important;
    border-color: #158a65 !important;
}

.btn-success:disabled {
    opacity: 0.65;
}

.custom-select {
    border-radius: 6px;
    height: 40px;
    font-size: 14px;
}

.form-label {
    font-weight: 400;
    font-size: 16px;
    margin-bottom: 6px;
}

.sku-input-wrapper {
    width: 100%;
}

.sku-input-container {
    display: flex;
    align-items: center;
    background-color: #fff;
    border: 1px solid #ced4da;
    border-radius: 4px;
    height: 40px;
    padding: 0 12px;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.sku-input-container:focus-within {
    border-color: #1AA179;
    box-shadow: 0 0 0 0.2rem rgba(26, 161, 121, 0.25);
}

.sku-input {
    flex: 1;
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    padding: 0 !important;
    font-size: 14px;
    background: transparent;
    color: #495057;
}

.sku-input::placeholder {
    color: #6c757d;
    opacity: 1;
}

.clear-icon {
    color: #6c757d;
    font-size: 14px;
    cursor: pointer;
    margin-left: 8px;
}

.clear-icon:hover {
    color: #495057;
}

.tags-wrapper {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    width: 100%;
}

.tags-container {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    flex: 1;
}

.tag {
    background-color: #007bff;
    color: white;
    padding: 4px 8px;
    border-radius: 16px;
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.tag-more {
    background-color: #6c757d;
    cursor: default;
}

.tag-remove-icon {
    cursor: pointer;
    font-size: 12px;
    opacity: 0.8;
}

.tag-remove-icon:hover {
    opacity: 1;
}

.btn-clear-all {
    background: none;
    border: none;
    color: #dc3545;
    cursor: pointer;
    padding: 6px 4px;
    font-size: 13px;
    opacity: 0.75;
}

.btn-clear-all:hover {
    opacity: 1;
}
</style>
